import type {
  CreateDmPageInput,
  UpdateDmPageInput,
  CreateDmShootingCategoryInput,
  UpdateDmShootingCategoryInput,
  CreateDmDesignCategoryInput,
  UpdateDmDesignCategoryInput,
  AddDmDesignCategorySizeInput,
  AddDmAccessUserInput,
  DmAccessType,
  CreateDmDesignProjectInput,
  UpdateDmDesignProjectInput,
  UpdateDmDesignProjectStatusInput,
  ReorderDmDesignProjectsInput,
  ListDmDesignProjectsQuery,
  CreateDmShootActivityInput,
  UpdateDmShootActivityInput,
  ListDmShootActivitiesQuery,
  CreateDmActivityInput,
  UpdateDmActivityInput,
  UpdateDmActivityStatusInput,
  AssignDmActivityInput,
  UploadDmActivityInput,
  ScheduleDmActivityInput,
  ListDmActivitiesQuery,
  ListDmCalendarQuery,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Shared types ──────────────────────────────────────────────────────────────

export interface DmUserRef {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  employee_code?: string;
}

export interface DmPageRef {
  id: string;
  name: string;
}

// ── Settings: Pages ───────────────────────────────────────────────────────────

export interface DmPage {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Settings: Shooting categories ────────────────────────────────────────────

export interface DmShootingCategory {
  id: string;
  name: string;
  description: string | null;
  requires_location: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Settings: Design categories ──────────────────────────────────────────────

export interface DmDesignCategorySize {
  id: string;
  name: string;
  sort_order: number;
}

export interface DmDesignCategory {
  id: string;
  name: string;
  is_active: boolean;
  sizes: DmDesignCategorySize[];
  created_at: string;
  updated_at: string;
}

// ── Settings: Access users ───────────────────────────────────────────────────

export interface DmAccessUser {
  id: string;
  user_id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  employee_code: string;
}

// ── Design projects ───────────────────────────────────────────────────────────

export type DmDesignProjectStatus = 'pending' | 'in_progress' | 'in_hold' | 'completed' | 'cancelled';

export interface DmDesignProjectCategory {
  id: string;
  category: { id: string; name: string } | null;
  size: { id: string; name: string } | null;
  custom_category_text: string | null;
  custom_size_text: string | null;
}

export interface DmDesignProjectImage {
  id: string;
  file_path: string;
  original_filename: string | null;
  sort_order: number;
}

export interface DmDesignProjectListItem {
  id: string;
  title: string;
  description: string | null;
  status: DmDesignProjectStatus;
  hold_reason: string | null;
  publish_date: string | null;
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  order_index: number;
  /** 1-based position within this project's status, spanning every page. */
  status_rank: number;
  designer: DmUserRef | null;
  assigned_by: DmUserRef | null;
  categories: DmDesignProjectCategory[];
  created_at: string;
  updated_at: string;
}

export interface DmDesignProjectHoldHistory {
  id: string;
  hold_reason: string | null;
  start_date: string;
  end_date: string | null;
  created_at: string;
}

export interface DmDesignProjectDetail extends DmDesignProjectListItem {
  created_by: DmUserRef | null;
  images: DmDesignProjectImage[];
  hold_history: DmDesignProjectHoldHistory[];
}

export interface DmDesignerMeta {
  categories: (DmDesignCategory & { sizes: DmDesignCategorySize[] })[];
  /** The status-permission whitelist — who may change a project's status. */
  designers: DmUserRef[];
  /** Who actually owns projects. The Designer filter's options. */
  projectDesigners: DmUserRef[];
  /** Who actually assigned projects. The Assigned By filter's options. */
  projectAssigners: DmUserRef[];
  hasStatusAccess: boolean;
  hasDesignCategoryAccess: boolean;
  hasReorderAccess: boolean;
  hasDeleteAccess: boolean;
  hasCompletedAccess: boolean;
}

/** Project count per status, following the list's filters and visibility. */
export interface DmDesignProjectCounts {
  total: number;
  pending: number;
  in_progress: number;
  in_hold: number;
  completed: number;
  cancelled: number;
}

// ── Shoot activities ──────────────────────────────────────────────────────────

export interface DmShootActivity {
  id: string;
  title: string;
  location: string | null;
  date_time: string;
  who_shot: DmUserRef | null;
  assigned: DmUserRef | null;
  assigned_users: DmUserRef[] | null;
  status: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
  source: 'shoot' | 'activity';
}

export interface DmShootMeta {
  shootTeam: DmUserRef[];
  assignList: DmUserRef[];
  hasCreateAccess: boolean;
}

// ── Activities ────────────────────────────────────────────────────────────────

export type DmActivityStatus = 'pending' | 'completed' | 'cancelled';

export interface DmActivity {
  id: string;
  title: string;
  location: string | null;
  date_time: string;
  status: DmActivityStatus;
  is_in_storage: boolean;
  upload_link: string | null;
  scheduled_date: string | null;
  shooting_category: { id: string; name: string; requires_location: boolean } | null;
  page: DmPageRef | null;
  updated_page: DmPageRef | null;
  assigned_by: DmUserRef | null;
  assigned_users: DmUserRef[];
  video_stats: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface DmActivityDetail extends DmActivity {
  created_by: DmUserRef | null;
  history_count: number;
}

export interface DmActivityHistory {
  id: string;
  action_type: string;
  field_name: string | null;
  old_value: unknown;
  new_value: unknown;
  created_by_id: string;
  created_by_name: string;
  description: string | null;
  created_at: string;
}

export interface DmActivitiesMeta {
  pages: DmPageRef[];
  shootingCategories: DmShootingCategory[];
  assignList: DmUserRef[];
  hasManageAccess: boolean;
  hasCalendarDeleteAccess: boolean;
  hasCompleteListDeleteAccess: boolean;
}

// ── Pagination ────────────────────────────────────────────────────────────────

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface PaginatedData<T> {
  data: T[];
  pagination: Pagination;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const dmTeamService = {

  // Settings: Pages
  listPages: () => api.get<{ data: DmPage[] }>('/dm-team/settings/pages').then(r => r.data),
  createPage: (body: CreateDmPageInput) => api.post<{ data: { id: string } }>('/dm-team/settings/pages', body).then(r => r.data),
  updatePage: (id: string, body: UpdateDmPageInput) => api.patch(`/dm-team/settings/pages/${id}`, body),
  deletePage: (id: string) => api.delete(`/dm-team/settings/pages/${id}`),

  // Settings: Shooting categories
  listShootingCategories: (activeOnly = true) =>
    api.get<{ data: DmShootingCategory[] }>(`/dm-team/settings/shooting-categories?activeOnly=${activeOnly}`).then(r => r.data),
  createShootingCategory: (body: CreateDmShootingCategoryInput) =>
    api.post<{ data: { id: string } }>('/dm-team/settings/shooting-categories', body).then(r => r.data),
  updateShootingCategory: (id: string, body: UpdateDmShootingCategoryInput) =>
    api.patch(`/dm-team/settings/shooting-categories/${id}`, body),
  deleteShootingCategory: (id: string) => api.delete(`/dm-team/settings/shooting-categories/${id}`),

  // Settings: Design categories
  listDesignCategories: (activeOnly = true) =>
    api.get<{ data: DmDesignCategory[] }>(`/dm-team/settings/design-categories?activeOnly=${activeOnly}`).then(r => r.data),
  createDesignCategory: (body: CreateDmDesignCategoryInput) =>
    api.post<{ data: { id: string } }>('/dm-team/settings/design-categories', body).then(r => r.data),
  updateDesignCategory: (id: string, body: UpdateDmDesignCategoryInput) =>
    api.patch(`/dm-team/settings/design-categories/${id}`, body),
  deleteDesignCategory: (id: string) => api.delete(`/dm-team/settings/design-categories/${id}`),
  addDesignCategorySize: (categoryId: string, body: AddDmDesignCategorySizeInput) =>
    api.post<{ data: { id: string } }>(`/dm-team/settings/design-categories/${categoryId}/sizes`, body).then(r => r.data),
  deleteDesignCategorySize: (categoryId: string, sizeId: string) =>
    api.delete(`/dm-team/settings/design-categories/${categoryId}/sizes/${sizeId}`),
  updateDesignCategorySize: (categoryId: string, sizeId: string, body: { name?: string }) =>
    api.patch(`/dm-team/settings/design-categories/${categoryId}/sizes/${sizeId}`, body),

  // Settings: Access control
  listAccessUsers: (type: DmAccessType) =>
    api.get<{ data: DmAccessUser[] }>(`/dm-team/settings/access/${type}`).then(r => r.data),
  addAccessUser: (type: DmAccessType, body: AddDmAccessUserInput) =>
    api.post<{ data: { id: string } }>(`/dm-team/settings/access/${type}`, body).then(r => r.data),
  removeAccessUser: (type: DmAccessType, userId: string) =>
    api.delete(`/dm-team/settings/access/${type}/${userId}`),

  // Designer
  getDesignerMeta: () =>
    api.get<{ data: DmDesignerMeta }>('/dm-team/designer/meta').then(r => r.data),
  getDesignProjectCounts: (query: Partial<ListDmDesignProjectsQuery> = {}) =>
    api.get<{ data: DmDesignProjectCounts }>('/dm-team/designer/counts', { params: query }).then(r => r.data),
  listDesignProjects: (query: Partial<ListDmDesignProjectsQuery> = {}) =>
    api.get<PaginatedData<DmDesignProjectListItem>>('/dm-team/designer', { params: query }).then(r => r.data),
  getDesignProject: (id: string) =>
    api.get<{ data: DmDesignProjectDetail }>(`/dm-team/designer/${id}`).then(r => r.data),
  createDesignProject: (body: CreateDmDesignProjectInput) =>
    api.post<{ data: { id: string } }>('/dm-team/designer', body).then(r => r.data),
  updateDesignProject: (id: string, body: UpdateDmDesignProjectInput) =>
    api.patch(`/dm-team/designer/${id}`, body),
  updateDesignProjectStatus: (id: string, body: UpdateDmDesignProjectStatusInput) =>
    api.patch(`/dm-team/designer/${id}/status`, body),
  deleteDesignProject: (id: string) => api.delete(`/dm-team/designer/${id}`),
  uploadProjectImages: (id: string, files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('images', f));
    return api.post<{ data: { id: string }[] }>(`/dm-team/designer/${id}/images`, form);
  },
  deleteProjectImage: (projectId: string, imageId: string) =>
    api.delete(`/dm-team/designer/${projectId}/images/${imageId}`),
  reorderDesignProjects: (body: ReorderDmDesignProjectsInput) =>
    api.put('/dm-team/designer/reorder', body),

  // Shoots
  getShootMeta: () =>
    api.get<{ data: DmShootMeta }>('/dm-team/shoots/meta').then(r => r.data),
  listShootActivities: (query: Partial<ListDmShootActivitiesQuery> = {}) =>
    api.get<PaginatedData<DmShootActivity>>('/dm-team/shoots', { params: query }).then(r => r.data),
  createShootActivity: (body: CreateDmShootActivityInput) =>
    api.post<{ data: { id: string } }>('/dm-team/shoots', body).then(r => r.data),
  updateShootActivity: (id: string, body: UpdateDmShootActivityInput) =>
    api.patch(`/dm-team/shoots/${id}`, body),
  deleteShootActivity: (id: string) => api.delete(`/dm-team/shoots/${id}`),

  // Activities
  getActivitiesMeta: () =>
    api.get<{ data: DmActivitiesMeta }>('/dm-team/activities/meta').then(r => r.data),
  listActivities: (query: Partial<ListDmActivitiesQuery> = {}) =>
    api.get<PaginatedData<DmActivity>>('/dm-team/activities', { params: query }).then(r => r.data),
  getActivity: (id: string) =>
    api.get<{ data: DmActivityDetail }>(`/dm-team/activities/${id}`).then(r => r.data),
  getActivityHistory: (id: string, page = 1, limit = 20) =>
    api.get<PaginatedData<DmActivityHistory>>(`/dm-team/activities/${id}/history`, { params: { page, limit } }).then(r => r.data),
  createActivity: (body: CreateDmActivityInput) =>
    api.post<{ data: { id: string } }>('/dm-team/activities', body).then(r => r.data),
  updateActivity: (id: string, body: UpdateDmActivityInput) =>
    api.patch(`/dm-team/activities/${id}`, body),
  updateActivityStatus: (id: string, body: UpdateDmActivityStatusInput) =>
    api.patch(`/dm-team/activities/${id}/status`, body),
  assignActivity: (id: string, body: AssignDmActivityInput) =>
    api.post(`/dm-team/activities/${id}/assign`, body),
  uploadActivity: (id: string, body: UploadDmActivityInput) =>
    api.post(`/dm-team/activities/${id}/upload`, body),
  scheduleActivity: (id: string, body: ScheduleDmActivityInput) =>
    api.post(`/dm-team/activities/${id}/schedule`, body),
  moveToStorage: (id: string, inStorage = true) =>
    api.post(`/dm-team/activities/${id}/storage`, { in_storage: inStorage }),
  deleteActivity: (id: string) => api.delete(`/dm-team/activities/${id}`),

  // Calendar
  getCalendarActivities: (query: ListDmCalendarQuery) =>
    api.get<{ data: DmActivity[] }>('/dm-team/activities/calendar', { params: query }).then(r => r.data),
  deleteCalendarActivity: (id: string) => api.delete(`/dm-team/calendar/${id}`),

  // Storage
  listStorage: (query: Partial<ListDmActivitiesQuery> = {}) =>
    api.get<PaginatedData<DmActivity>>('/dm-team/storage', { params: query }).then(r => r.data),
};
